import React from 'react';
import { formatNumber } from '../../common/lib/formatNumber';
import type { BoatAd, SearchKey } from '../../types';
import { SearchAd } from '../search/SearchAd';
import { useSearchAdClasses } from '../search/SearchAdClasses';
import type {
    BaseSearchAdProps,
    SearchAdLayout,
} from '../search/SearchAdTypes';
import { BoatHeading } from '../search/boat/BoatHeading';
import { BoatImage } from '../search/boat/BoatImage';
import { LegendaryAdImageOptions } from './LegendaryAdImageOptions';
import { SecondaryTag } from './SecondayTag';

const gridClasses = {
    mobile: {
        ad: '',
        imageWrapper: 'mb-8',
        image: 'rounded-8 border s-border',
        location: 'pr-44 order-first space-x-16 text-xs s-text-subtle',
        content: 'flex flex-col',
        heading: 'pr-44 break-words mb-8',
        summary:
            'mb-8 flex justify-between space-x-12 whitespace-nowrap font-bold',
        organisation: 'flex flex-col text-xs',
        ribbon: 'absolute top-10 left-10 pointer-events-none',
        favorite: 'relative order-first',
        favoriteButton: 'absolute top-0 right-0',
    },
    desktop: {},
};

const listClasses = {
    mobile: {
        ad: 'grid grid-cols-3 grid-rows-1 gap-x-16',
        imageWrapper: '',
        image: 'rounded-8 border s-border',
        location:
            'col-span-2 order-first pr-44 space-x-16 text-xs s-text-subtle',
        content: 'col-span-2 flex flex-col',
        heading: 'mb-8 pr-44 break-words',
        summary:
            'mb-8 flex justify-between space-x-12 whitespace-nowrap font-bold',
        organisation: 'flex flex-col text-xs',
        ribbon: 'absolute top-10 left-10 pointer-events-none',
        favorite: '',
        favoriteButton: '',
    },
    desktop: {},
};

type LegendaryBoatSearchAdProps = BaseSearchAdProps & {
    ad: BoatAd;
    imageSizes?: string;
    layout: SearchAdLayout;
    searchKey: SearchKey;
};

export function LegendaryBoatSearchAd(props: LegendaryBoatSearchAdProps) {
    const {
        ad,
        className,
        favorite,
        image,
        imageSizes,
        label,
        layout,
        searchKey,
        ...searchAdProps
    } = props;

    const layoutClasses = layout === 'grid' ? gridClasses : listClasses;
    const classes = useSearchAdClasses({
        mode: 'responsive',
        mobileClasses: layoutClasses.mobile,
        desktopClasses: layoutClasses.desktop,
    });

    const edialogLabel = ad.labels
        ?.filter((label) => label.type === 'PRIMARY')
        .find((label) => label.id === 'edialog');
    const soldLabel = ad.labels
        ?.filter((label) => label.type === 'PRIMARY')
        .find((label) => label.id === 'sold');
    const promotedLabel = ad.labels
        ?.filter((label) => label.type === 'PRIMARY')
        ?.find((label) => label.id === 'fus');

    const isPromoted = Boolean(promotedLabel);

    const legendaryPromotedLabel = label ? (
        <SecondaryTag className="order-first" render={label} />
    ) : (
        <SecondaryTag
            className="order-first"
            label={promotedLabel}
            type="info"
        />
    );

    return (
        <SearchAd
            adId={ad.id}
            className={`${classes.ad} ${className}`}
            favorite={layout === 'list' ? favorite : undefined}
            legendary
            {...searchAdProps}
        >
            <div className={classes.imageWrapper}>
                <LegendaryAdImageOptions>
                    <BoatImage
                        ad={ad}
                        image={image}
                        imageClass={classes.image}
                        sizes={imageSizes}
                    />
                </LegendaryAdImageOptions>
            </div>
            <div className={classes.content}>
                <BoatHeading ad={ad} className={classes.heading} />
                {favorite && layout === 'grid' && (
                    <div className={classes.favorite}>
                        <span className={classes.favoriteButton}>
                            {favorite()}
                        </span>
                    </div>
                )}
                {legendaryPromotedLabel}
                {soldLabel && !isPromoted && (
                    <SecondaryTag
                        className="order-first"
                        label={soldLabel}
                        type="warning"
                    />
                )}
                {edialogLabel && !isPromoted && (
                    <SecondaryTag
                        className="order-first"
                        label={edialogLabel}
                        type="positive"
                    />
                )}
                <div className={classes.location}>
                    {ad.location && <span>{ad.location}</span>}
                </div>
                {(ad.price || ad.length || ad.year) && (
                    <div className={classes.summary}>
                        {ad.year > 0 && <span>{ad.year}</span>}
                        {ad.length && ad.length > 0 && (
                            <span>{formatNumber(ad.length)} fot</span>
                        )}
                        {ad.price.amount > 0 && (
                            <span>
                                {formatNumber(ad.price.amount)}
                                {ad.price.price_unit}
                            </span>
                        )}
                    </div>
                )}
                <div className={classes.organisation}>
                    {searchKey === 'SEARCH_ID_BOAT_USED' &&
                        ad.organisation_name && (
                            <span>{ad.organisation_name}</span>
                        )}
                    <BulletPointsRow ad={ad} searchKey={searchKey} />
                </div>
            </div>
        </SearchAd>
    );
}

const BulletPointsRow = ({
    ad,
    searchKey,
}: {
    ad: BoatAd;
    searchKey: SearchKey;
}) => {
    function getBulletPointsText() {
        const showWidth = [
            'SEARCH_ID_BOAT_DOCK_WANTED',
            'SEARCH_ID_BOAT_DOCK',
        ].includes(searchKey);
        const showDealer = ['SEARCH_ID_BOAT_USED'].includes(searchKey);

        let output: string[] = [];
        if (showDealer && ad.dealer_segment)
            output = output.concat(ad.dealer_segment);
        if (ad.motor_fuel) output = output.concat(ad.motor_fuel);
        if (ad.motor_type) output = output.concat(ad.motor_type);
        if (ad.max_speed) output = output.concat(ad.max_speed + ' knop');
        if (showWidth && ad.width) output = output.concat(ad.width + ' cm');

        return output.join(' • ');
    }

    const text = getBulletPointsText();

    return text ? <span>{text}</span> : null;
};
